-- | GraphQL queries and types used for the search widget.
module API.Search
    ( SearchTeacher
    , SearchSemester
    , SearchClass
    , SearchData, SearchDefaults
    , searchDataQuery
    , teacherQuery, teacherSearchAff
    , semesterQuery, semesterSearchAff
    , classesQuery, classSearchAff
    , searchDefaultsQuery
    , emptySearchData, emptySearchDefaults
    , BaseSearchQuery, SearchQuery, queryFields, queryObj, queryObj', queryVars
    , SearchVars
) where

import Common

import API.Pagination (unpaged)
import API.Types (ClassID, SemesterID, TeacherID)
import Data.Argonaut (Json)
import Data.Argonaut as J
import Data.Array (catMaybes, concatMap)
import Foreign.Object (Object)
import Foreign.Object as O
import GraphQL.Compound (CompQuery, composeQueries)
import GraphQL.Query (class ToVars, Query, QueryVar(..), buildQuery)
import GraphQL.Type (QType(..))
import Widgets.BetterSelect (SearchAff(..), simpleFrontendSearch, wrapSelectOption)


type SearchTeacher =
    { id :: TeacherID
    , name :: String
    }

teacherQuery :: String -> Query (Array SearchTeacher)
teacherQuery search = unpaged $ buildQuery "teachers" { search }

teacherSearchAff :: SearchAff SearchTeacher
teacherSearchAff = BackendSearch \search -> wrapSelectOption _.name $ teacherQuery search


type SearchSemester =
    { semester :: SemesterID
    , name :: String
    }

semesterQuery :: Query (Array SearchSemester)
semesterQuery = buildQuery "semesters" {}

semesterSearchAff :: SearchAff SearchSemester
semesterSearchAff = simpleFrontendSearch _.label $ wrapSelectOption _.name $ semesterQuery


type SearchClass =
    { id :: ClassID
    , name :: String
    }

type ClassesQueryResponse =
    { teachers :: Array { groups :: Array SearchClass }
    , students :: Array { groups :: Array SearchClass }
    }

classesQuery :: Query (Array SearchClass)
classesQuery = process <$> buildQuery "me" {}
  where
    process :: ClassesQueryResponse -> Array SearchClass
    process { teachers, students } = groups teachers <> groups students
    groups = concatMap _.groups

classSearchAff :: SearchAff SearchClass
classSearchAff = simpleFrontendSearch _.label $ wrapSelectOption _.name $ classesQuery


type SearchDefaults =
    { teacher :: Maybe SearchTeacher
    , semester :: Maybe SearchSemester
    , group :: Maybe SearchClass
    }

emptySearchDefaults :: SearchDefaults
emptySearchDefaults = { teacher: Nothing, semester: Nothing, group: Nothing }

searchDefaultsQuery :: Query SearchDefaults
searchDefaultsQuery = buildQuery "defaultSelections" {}


-- | Data returned from `searchDataQuery`. Contains lists of teachers and semesters.
type SearchData =
    { teachers :: Array SearchTeacher
    , semesters :: Array SearchSemester
    , classes :: Array SearchClass
    , defaults :: SearchDefaults
    }

emptySearchData :: SearchData
emptySearchData = { teachers: [], semesters: [], classes: [], defaults: emptySearchDefaults }

-- | Query the backend for data to populate the search widget.
searchDataQuery :: CompQuery SearchData
searchDataQuery = composeQueries "SearchData"
    { teachers: unpaged $ buildQuery "teachers" {}
    , semesters: semesterQuery
    , classes: classesQuery
    , defaults: searchDefaultsQuery
    }

-- | Options passed to to API for searching.
type SearchQuery = BaseSearchQuery ()

type BaseSearchQuery r =
    { teacher :: Maybe TeacherID
    , semester :: Maybe SemesterID
    , class :: Maybe ClassID
    , search :: Maybe String
    | r
    }

queryFields :: forall r. BaseSearchQuery r -> Array (Tuple String String)
queryFields q = catMaybes
    [ Tuple "teacher_id" "[ID!]" <$ q.teacher
    , Tuple "class_id" "ID!" <$ q.class
    , Tuple "semester" "String!" <$ q.semester
    , Tuple "search" "String!" <$ q.search
    ]

data SearchVars = SearchVars SearchQuery

queryVars :: forall r. BaseSearchQuery r -> SearchVars
queryVars { teacher, semester, search, class: class' } = SearchVars { teacher, semester, search, class: class' }

instance ToVars SearchVars where
    toVars (SearchVars q) = catMaybes
        [ QueryVar "teacher_id" (Nullable $ ArrayOf $ QType "ID") <$ q.teacher
        , QueryVar "class_id" (QType "ID") <$ q.class
        , QueryVar "semester" (QType "String") <$ q.semester
        , QueryVar "search" (QType "String") <$ q.search
        ]
    encodeVars (SearchVars q) = queryObj' q
    varFiles _ = []

queryObj :: forall r. BaseSearchQuery r -> Json
queryObj = J.fromObject <<< queryObj'

queryObj' :: forall r. BaseSearchQuery r -> Object Json
queryObj' q = O.fromFoldable $ catMaybes
    [ (\a -> Tuple "teacher_id" $ J.encodeJson a) <$> q.teacher
    , (\a -> Tuple "class_id" $ J.encodeJson a) <$> q.class
    , (\a -> Tuple "semester" $ J.encodeJson a) <$> q.semester
    , (\a -> Tuple "search" $ J.encodeJson a) <$> q.search
    ]
